import { useEffect, useState } from "react";
import { site, openWhatsApp } from "@/content/liane-site.ar";
import { useReveal } from "@/hooks/use-reveal";
import { WhatsAppIcon } from "./WhatsAppIcon";

type Fields = {
  occasion: string;
  date: string;
  theme: string;
  soap: string;
  quantity: string;
  notes: string;
};

type Errors = Partial<Record<keyof Fields, string | undefined>>;

const empty: Fields = {
  occasion: "",
  date: "",
  theme: "",
  soap: "",
  quantity: "",
  notes: "",
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const buildMessage = (v: Fields) =>
  [
    "مرحباً ليان صوب 🌿",
    "أرغب بطلب توزيعات صابون:",
    `• نوع المناسبة: ${v.occasion}`,
    `• تاريخ المناسبة: ${v.date}`,
    `• ثيم التغليف: ${v.theme}`,
    `• نوع الصابون: ${v.soap}`,
    `• الكمية المطلوبة: ${v.quantity}`,
    `• ملاحظات: ${v.notes.trim() || "لا يوجد"}`,
  ].join("\n");

// 16px font-size prevents iOS Safari auto-zoom on focus; 3rem = comfy touch target
const fieldBase =
  "h-12 w-full rounded-xl border bg-l-card px-3.5 text-start text-[16px] leading-none text-l-foreground outline-none transition-colors focus-visible:border-l-accent-strong sm:h-[3.25rem] sm:rounded-2xl sm:px-4";

export function OrderBuilder({ presetOccasion }: { presetOccasion: string }) {
  const reveal = useReveal<HTMLDivElement>();
  const [values, setValues] = useState<Fields>(empty);
  const [errors, setErrors] = useState<Errors>({});

  useEffect(() => {
    if (presetOccasion) {
      setValues((v) => ({ ...v, occasion: presetOccasion }));
      setErrors((e) => ({ ...e, occasion: undefined }));
    }
  }, [presetOccasion]);

  const set = (name: keyof Fields, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const f = site.order.fields;

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Errors = {};
    if (!values.occasion) next.occasion = f.occasion.error;
    if (!values.date) next.date = f.date.error;
    if (!values.theme) next.theme = f.theme.error;
    if (!values.soap) next.soap = f.soap.error;
    if (!values.quantity) next.quantity = f.quantity.error;
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      document.getElementById(`field-${first}`)?.focus();
      return;
    }
    openWhatsApp(buildMessage(values));
  };

  const selects: Array<{
    name: keyof Fields;
    label: string;
    placeholder: string;
    options: readonly string[];
  }> = [
    {
      name: "occasion",
      label: f.occasion.label,
      placeholder: f.occasion.placeholder,
      options: f.occasion.options,
    },
    {
      name: "theme",
      label: f.theme.label,
      placeholder: f.theme.placeholder,
      options: f.theme.options,
    },
    { name: "soap", label: f.soap.label, placeholder: f.soap.placeholder, options: f.soap.options },
    {
      name: "quantity",
      label: f.quantity.label,
      placeholder: f.quantity.placeholder,
      options: f.quantity.options,
    },
  ];

  return (
    <section id="order" className="scroll-mt-20 py-12 sm:py-16 lg:py-24">
      <div ref={reveal.ref} className={`container-liane ${reveal.className}`}>
        <div className="mx-auto max-w-3xl rounded-3xl bg-l-primary px-4 py-8 shadow-l-lift sm:rounded-l4xl sm:px-8 sm:py-11 lg:px-12 lg:py-14">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-xl font-bold text-l-primary-foreground sm:text-3xl lg:text-4xl">
              {site.order.title}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-l-primary-foreground/75 sm:mt-4 sm:text-base">
              {site.order.sub}
            </p>
          </div>

          <form onSubmit={onSubmit} noValidate className="mx-auto mt-7 max-w-xl sm:mt-9">
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
              {selects.slice(0, 1).map((s) => (
                <SelectField
                  key={s.name}
                  name={s.name}
                  label={s.label}
                  placeholder={s.placeholder}
                  options={s.options}
                  value={values[s.name]}
                  error={errors[s.name]}
                  onChange={set}
                />
              ))}

              <div className="text-start">
                <label
                  htmlFor="field-date"
                  className="mb-1.5 block text-[13px] font-medium text-l-primary-foreground sm:mb-2 sm:text-sm"
                >
                  {f.date.label} <span className="text-l-accent">*</span>
                </label>
                <input
                  id="field-date"
                  type="date"
                  min={todayISO()}
                  value={values.date}
                  onChange={(e) => set("date", e.target.value)}
                  aria-invalid={Boolean(errors.date)}
                  className={`${fieldBase} ${errors.date ? "border-l-destructive" : "border-transparent"}`}
                />
                {errors.date && <ErrorText>{errors.date}</ErrorText>}
              </div>

              {selects.slice(1).map((s) => (
                <SelectField
                  key={s.name}
                  name={s.name}
                  label={s.label}
                  placeholder={s.placeholder}
                  options={s.options}
                  value={values[s.name]}
                  error={errors[s.name]}
                  onChange={set}
                />
              ))}

              <div className="text-start sm:col-span-2">
                <label
                  htmlFor="field-notes"
                  className="mb-1.5 block text-[13px] font-medium text-l-primary-foreground sm:mb-2 sm:text-sm"
                >
                  {f.notes.label}
                </label>
                <textarea
                  id="field-notes"
                  rows={3}
                  value={values.notes}
                  placeholder={f.notes.placeholder}
                  onChange={(e) => set("notes", e.target.value)}
                  className="w-full resize-y rounded-xl border border-transparent bg-l-card p-3.5 text-start text-[16px] leading-relaxed text-l-foreground outline-none transition-colors placeholder:text-l-muted-foreground focus-visible:border-l-accent-strong sm:rounded-2xl sm:p-4"
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-6 inline-flex min-h-[3.25rem] w-full items-center justify-center gap-2 rounded-full bg-l-accent px-6 text-[15px] font-bold text-l-accent-strong-foreground transition-transform hover:brightness-105 active:scale-[0.99] sm:mt-8 sm:min-h-14 sm:px-8 sm:text-base"
            >
              <WhatsAppIcon className="size-5 shrink-0" />
              {site.order.submit}
            </button>
            <p className="mt-3 text-center text-[13px] leading-relaxed text-l-primary-foreground/70 sm:mt-4 sm:text-sm">
              {site.order.microcopy}
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function ErrorText({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" className="mt-1.5 text-[13px] text-l-accent sm:text-sm">
      {children}
    </p>
  );
}

function SelectField({
  name,
  label,
  placeholder,
  options,
  value,
  error,
  onChange,
}: {
  name: keyof Fields;
  label: string;
  placeholder: string;
  options: readonly string[];
  value: string;
  error: string | undefined;
  onChange: (name: keyof Fields, value: string) => void;
}) {
  return (
    <div className="text-start">
      <label
        htmlFor={`field-${name}`}
        className="mb-1.5 block text-[13px] font-medium text-l-primary-foreground sm:mb-2 sm:text-sm"
      >
        {label} <span className="text-l-accent">*</span>
      </label>
      <div className="relative">
        <select
          id={`field-${name}`}
          value={value}
          aria-invalid={Boolean(error)}
          onChange={(e) => onChange(name, e.target.value)}
          className={`${fieldBase} appearance-none pe-10 ${error ? "border-l-destructive" : "border-transparent"} ${value ? "" : "text-l-muted-foreground"}`}
        >
          <option value="" disabled>
            {placeholder}
          </option>
          {options.map((o) => (
            <option key={o} value={o} className="text-l-foreground">
              {o}
            </option>
          ))}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 20 20"
          fill="none"
          className="pointer-events-none absolute inset-y-0 end-3.5 my-auto size-4 text-l-muted-foreground"
        >
          <path
            d="M5 7.5 10 12.5 15 7.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
